import {
  CircularProgress,
  makeStyles,
  Table,
  TableRow,
  Tooltip
} from '@material-ui/core';
import IconTooltip from 'components/Base/Tooltip/Tooltip'; // Custom Tooltip renamed as IconTooltip
import { BoxWrapper, Button, Modal } from 'components/Base';
import React, { useState, useEffect } from 'react';
import TableComponents from 'components/Table';
import api from 'api';
import Checkbox from 'components/Checkbox/Checkbox';
import { linkColor, secondaryText } from 'utils/colors';
import { Notify, pluralise } from 'utils';
import Icons from 'components/Icons';
import { isEqual } from 'lodash';

const useStyles = makeStyles({
  flex: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },
  link: {
    textDecoration: 'none',
    fontWeight: 600,
    color: linkColor,
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  btnContainer: {
    marginTop: '1rem',
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between'
  },
  loaderWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '10rem'
  }
});

const UserManagement = () => {
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(true);
  const [storedUsers, setStoredUsers] = useState([] as any);
  const [users, setUsers] = useState([] as any);
  const [editingUser, setEditingUser] = useState(null as any);
  const [selectedUsers, setSelectedUsers] = useState([] as any);
  const [isEditParaplannerModalOpen, setIsEditParaplannerModalOpen] =
    useState(false);

  useEffect(() => {
    setIsLoading(true);
    api.getFirmUsers().then(({ data }: any) => {
      const updatedResults = data.results.map((user: any) => {
        if (user.email.includes('fintegrate')) {
          return {
            ...user,
            full_name: `${user.full_name} (Fintegrate Support)`
          };
        }
        return user;
      });

      setStoredUsers(updatedResults);
      setIsLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setUsers(storedUsers);
  }, [storedUsers]);

  useEffect(() => {
    if (editingUser) {
      setSelectedUsers(editingUser.advisers);
    }
  }, [editingUser]);

  const updateUser = (index: number, updates: any) => {
    const updatedUsers = [...users];
    const user = updatedUsers[index];
    updatedUsers.splice(index, 1, { ...user, ...updates });
    setUsers(updatedUsers);
  };

  const getAdviserList = (advisers: any, display = 2) => {
    const ids = advisers.map((adviser: any) => adviser.id);

    const adviserList = users
      .filter(
        (user: any) => user.access_level === 'Adviser' && ids.includes(user.id)
      )
      .map((user: any) => user.full_name);

    const advisersToDisplay = adviserList.splice(0, display);
    const tooltipText = adviserList.join(', ');

    return (
      <>
        {advisersToDisplay.join(adviserList.length ? ', ' : ' & ')}
        {adviserList.length > 0 ? (
          <>
            {' & '}
            <Tooltip title={tooltipText} arrow>
              <span>
                {adviserList.length} {pluralise(adviserList.length, 'other')}
              </span>
            </Tooltip>
          </>
        ) : (
          <></>
        )}
      </>
    );
  };

  const toggleUser = (user: any) => {
    const updatedUsers = [...selectedUsers];
    const exists = updatedUsers
      .map((selectedUser: any) => selectedUser.id)
      .includes(user.id);

    if (exists) {
      setSelectedUsers(
        updatedUsers.filter((selectedUser: any) => selectedUser.id !== user.id)
      );
    } else {
      setSelectedUsers([...updatedUsers, user]);
    }
  };

  const save = () => {
    api
      .updateFirmUsers(
        users.filter((user: any) => {
          const match = storedUsers.find(
            (storedUser: any) => storedUser.id === user.id
          );
          return !isEqual(user, match);
        })
      )
      .then(() => {
        setStoredUsers(users);
        Notify.success('Users updated successfully.');
      })
      .catch(() => {
        Notify.error('Users could not be updated.');
      });
  };

  return (
    <>
      <BoxWrapper title="User Management">
        {isLoading ? (
          <div className={classes.loaderWrapper}>
            <CircularProgress size="80px" />
          </div>
        ) : (
          <>
            <Table>
              <TableComponents.TableHead
                cells={[
                  <div key="name-cell">
                    <span>Name</span>
                  </div>,
                  <div
                    key="access-level-cell"
                    style={{ display: 'flex', alignItems: 'end', gap: '.5rem' }}
                  >
                    <span>Access Level</span>
                    <IconTooltip
                      key="access-level-tooltip"
                      text="Displays the role assigned to each user (e.g., Adviser, Paraplanner)"
                    />
                  </div>,
                  <div
                    key="client-visibility-cell"
                    style={{ display: 'flex', alignItems: 'end', gap: '.5rem' }}
                  >
                    <span>Client Visibility</span>
                    <IconTooltip
                      key="client-visibility-tooltip"
                      text="Controls the scope of clients each user can view. Select Own Clients if the user should only see clients they manage, or All Clients to grant visibility across all clients in the firm."
                    />
                  </div>,
                  <div
                    key="firm-admin-cell"
                    style={{ display: 'flex', alignItems: 'end', gap: '.5rem' }}
                  >
                    <span>Firm Admin</span>
                    <IconTooltip
                      key="firm-admin-tooltip"
                      text="Indicates if a user has administrative rights to adjust settings and manage the centralised investment proposition. "
                    />
                  </div>
                ]}
              />
              <TableComponents.TableBody>
                {users.map((user: any, index: number) => (
                  <TableRow key={index}>
                    <TableComponents.TableCell>
                      {user.full_name}
                    </TableComponents.TableCell>
                    <TableComponents.TableCell>
                      {user.access_level}
                    </TableComponents.TableCell>
                    <TableComponents.TableCell>
                      {user.access_level === 'Adviser' ? (
                        <>
                          <div
                            style={{
                              display: 'flex',
                              gap: '0.5rem',
                              alignItems: 'center'
                            }}
                          >
                            <Checkbox
                              onClick={() => {
                                updateUser(index, { client_visibility: 1 });
                              }}
                              label={'Own clients'}
                              isChecked={user.client_visibility === 1}
                            />
                            <Checkbox
                              onClick={() => {
                                updateUser(index, { client_visibility: 2 });
                              }}
                              label={'All clients'}
                              isChecked={user.client_visibility === 2}
                            />
                          </div>
                        </>
                      ) : (
                        <div className={classes.flex}>
                          {getAdviserList(user.advisers)}{' '}
                          <Icons.EditIcon
                            width="2.2rem"
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              setEditingUser(user);
                              setIsEditParaplannerModalOpen(true);
                            }}
                          />
                        </div>
                      )}
                    </TableComponents.TableCell>
                    <TableComponents.TableCell>
                      <Checkbox
                        disabled={true}
                        label=""
                        isChecked={user.firm_admin}
                        onClick={() => {
                          //
                        }}
                      />
                    </TableComponents.TableCell>
                  </TableRow>
                ))}
              </TableComponents.TableBody>
            </Table>
            <div style={{ padding: '0 1rem 1rem 1rem' }}>
              <div style={{ fontSize: '0.9em', color: secondaryText }}>
                <p>
                  Firm admin users can control visibility and view user access
                  and permissions.
                </p>
                <p>
                  To make additional changes, such as updating user access
                  levels or adding/removing users, please contact Fintegrate
                  support at{' '}
                  <a
                    className={classes.link}
                    href="mailto:support@fintegrate.co.uk"
                  >
                    support@fintegrate.co.uk
                  </a>
                </p>
              </div>
              <Button onClick={save}>Save</Button>
            </div>
          </>
        )}
      </BoxWrapper>
      <Modal
        isOpen={isEditParaplannerModalOpen}
        closeModalFunc={() => setIsEditParaplannerModalOpen(false)}
      >
        <BoxWrapper
          title={`Update client visibility for ${editingUser?.full_name}`}
          closeModal={() => setIsEditParaplannerModalOpen(false)}
        >
          <div style={{ padding: '1rem' }}>
            {users
              .filter((user: any) => user.access_level === 'Adviser')
              .map((user: any, index: number) => {
                return (
                  <div key={index}>
                    <Checkbox
                      onClick={() => {
                        toggleUser(user);
                      }}
                      label={user.full_name}
                      isChecked={selectedUsers
                        .map((selectedUser: any) => selectedUser.id)
                        .includes(user.id)}
                    />
                  </div>
                );
              })}
            <div className={classes.btnContainer}>
              <Button
                onClick={() => setIsEditParaplannerModalOpen(false)}
                color="primary"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (!selectedUsers.length) {
                    Notify.error('At least one adviser must be selected.');
                    return;
                  }
                  setIsEditParaplannerModalOpen(false);
                  updateUser(
                    users.findIndex((user: any) => editingUser.id === user.id),
                    { advisers: selectedUsers }
                  );
                }}
              >
                Save
              </Button>
            </div>
          </div>
        </BoxWrapper>
      </Modal>
    </>
  );
};

export default UserManagement;
